import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { getUserProfile, UserProfile } from '../../services/userProfileService';

const Statistics: React.FC = () => {
    const history = useHistory();
    const [user, setUser] = useState<UserProfile | null>(null);

    useEffect(() => {
        const profile = getUserProfile();
        setUser(profile);
    }, []);

    if (!user) {
        return <div style={{ textAlign: 'center', padding: '40px 20px' }}>Loading statistics...</div>;
    }

    const categories = [
        { id: 'russia', name: '🪆 Russіа', color: '#D4A574' },
        { id: 'nature', name: '🌲 Nature', color: '#8B9D6D' },
        { id: 'animals', name: '🐾 Animals', color: '#B8956A' },
        { id: 'seasons', name: '🍂 Seasons', color: '#D4A574' },
        { id: 'cozy', name: '🏡 Cozy', color: '#C99A8B' },
        { id: 'ocean', name: '🌊 Ocean', color: '#7BA5B8' },
    ];

    return (
        <div style={{
            padding: '20px',
            maxWidth: '600px',
            margin: '0 auto',
            paddingBottom: '100px',
        }}>
            <button
                onClick={() => history.goBack()}
                style={{
                    marginBottom: '20px',
                    padding: '10px 16px',
                    background: theme.colors.bgSecondary,
                    border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme.colors.textPrimary,
                    fontWeight: 600,
                }}
            >
                ← Back
            </button>

            <h1 style={{
                fontSize: '28px',
                fontWeight: 700,
                color: theme.colors.textPrimary,
                marginBottom: '24px',
            }}>
                📊 Your Statistics
            </h1>

            {/* Overview Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                marginBottom: '32px',
            }}>
                <div style={{
                    padding: '16px',
                    backgroundColor: theme.colors.cardBg,
                    borderRadius: '12px',
                    textAlign: 'center',
                }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🧩</div>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: theme.colors.textPrimary,
                    }}>
                        {user.stats.puzzlesCompleted}
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                    }}>
                        Total Puzzles
                    </div>
                </div>

                <div style={{
                    padding: '16px',
                    backgroundColor: theme.colors.cardBg,
                    borderRadius: '12px',
                    textAlign: 'center',
                }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>⏱️</div>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: theme.colors.textPrimary,
                    }}>
                        {Math.round(user.stats.totalPlayTime / 60)}h
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                    }}>
                        Play Time
                    </div>
                </div>

                <div style={{
                    padding: '16px',
                    backgroundColor: theme.colors.cardBg,
                    borderRadius: '12px',
                    textAlign: 'center',
                }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔥</div>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#FF6B6B',
                    }}>
                        {user.stats.streakDays}
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                    }}>
                        Day Streak
                    </div>
                </div>

                <div style={{
                    padding: '16px',
                    backgroundColor: theme.colors.cardBg,
                    borderRadius: '12px',
                    textAlign: 'center',
                }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>📅</div>
                    <div style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: theme.colors.textPrimary,
                    }}>
                        {new Date(user.stats.lastPlayDate).toLocaleDateString()}
                    </div>
                    <div style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                    }}>
                        Last Played
                    </div>
                </div>
            </div>

            {/* By Category */}
            <h2 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: theme.colors.textPrimary,
                marginBottom: '16px',
            }}>
                Puzzles by Category
            </h2>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                marginBottom: '32px',
            }}>
                {categories.map(cat => {
                    const count = user.stats.puzzlesCompletedByCategory[cat.id] || 0;
                    return (
                        <div
                            key={cat.id}
                            style={{
                                padding: '16px',
                                backgroundColor: theme.colors.cardBg,
                                borderRadius: '12px',
                                textAlign: 'center',
                                border: count > 0 ? `2px solid ${cat.color}` : 'none',
                            }}
                        >
                            <div style={{ fontSize: '20px', marginBottom: '8px' }}>
                                {cat.name.split(' ')[0]}
                            </div>
                            <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: count > 0 ? cat.color : theme.colors.textSecondary,
                            }}>
                                {count}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Progress */}
            <h2 style={{
                fontSize: '18px',
                fontWeight: 700,
                color: theme.colors.textPrimary,
                marginBottom: '16px',
            }}>
                Level Progress
            </h2>

            <div style={{
                padding: '20px',
                backgroundColor: theme.colors.cardBg,
                borderRadius: '12px',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                }}>
                    <span style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                        Level {user.level}
                    </span>
                    <span style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                        {user.xp % 1000} / 1000 XP
                    </span>
                </div>

                <div style={{
                    width: '100%',
                    height: '16px',
                    backgroundColor: theme.colors.cardBorder,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        width: `${(user.xp % 1000) / 10}%`,
                        height: '100%',
                        backgroundColor: theme.colors.accent,
                        borderRadius: '8px',
                        transition: 'width 0.3s ease',
                    }} />
                </div>

                <div style={{
                    marginTop: '12px',
                    fontSize: '12px',
                    color: theme.colors.textSecondary,
                }}>
                    Total XP: {user.xp} / {user.level * 1000} needed for next level
                </div>
            </div>
        </div>
    );
};

export default Statistics;
